import {
  AIP_NAME_BUDGET, ARN_RE, EMAIL_RE, Errors, FmWizardState, NEW_ENV_VALUE,
  ROLE_NAME_BUDGET, SERVICE_NAME_MAX, SHORT_NAME_MAX, SLUG_RE,
} from './types';

/**
 * Resolves the environment name that AWS resource names will embed: the new environment name
 * when creating one, otherwise the selected existing environment's name. Returns '' when unknown.
 */
export function resolveEnvName(
  s: FmWizardState,
  environments: Array<{ documentId: string; name: string }>,
): string {
  if (s.environmentId === NEW_ENV_VALUE) return s.newEnvName.trim();
  return environments.find((e) => e.documentId === s.environmentId)?.name ?? '';
}

/** Validates "Bundle info" (step 0). */
export function validateBundleInfo(
  s: FmWizardState,
  environments: Array<{ documentId: string; name: string }> = [],
): Errors {
  const e: Errors = {};
  if (!s.projectId) e.projectId = 'Select a project.';
  if (!s.environmentId) e.environmentId = 'Select an environment.';
  if (s.environmentId === NEW_ENV_VALUE && !s.newEnvName.trim())
    e.newEnvName = 'Enter a name for the new environment.';
  if (!s.team) e.team = 'Select a team.';
  const svc = s.serviceName.trim();
  if (!svc) e.serviceName = 'Service name is required.';
  else if (!SLUG_RE.test(s.serviceName))
    e.serviceName = 'Use lowercase letters, numbers and hyphens; must start with a letter or number.';
  else if (svc.length > SERVICE_NAME_MAX)
    e.serviceName = `Service name is ${svc.length} characters — max ${SERVICE_NAME_MAX}. `
      + `IAM role name "${svc}-foundation-models-role-<env>" would exceed 64 characters.`;
  else {
    const envName = resolveEnvName(s, environments);
    if (envName && svc.length + envName.length > ROLE_NAME_BUDGET)
      e.serviceName = `Service name (${svc.length}) + environment "${envName}" (${envName.length}) `
        + `exceeds ${ROLE_NAME_BUDGET} characters. `
        + `IAM role name "${svc}-foundation-models-role-${envName}" would exceed 64 characters.`;
  }
  return e;
}

/** Validates "Models" (step 1). */
export function validateModels(
  s: FmWizardState,
  environments: Array<{ documentId: string; name: string }> = [],
): Errors {
  const e: Errors = {};
  if (s.models.length === 0) {
    e.models = 'Select at least one model.';
    return e;
  }
  const svc = s.serviceName.trim();
  const envName = resolveEnvName(s, environments);
  const seen = new Set<string>();
  s.models.forEach((m, i) => {
    const shortName = m.shortName.trim();
    if (!shortName || !SLUG_RE.test(m.shortName)) {
      e[`models.${i}.shortName`] = 'Use lowercase letters, numbers and hyphens; must start with a letter or number.';
    } else if (shortName.length > SHORT_NAME_MAX) {
      e[`models.${i}.shortName`] = `Short name is ${shortName.length} characters — max ${SHORT_NAME_MAX}. `
        + `Inference profile name "${svc}-${shortName}-<env>" would exceed 64 characters.`;
    } else if (seen.has(shortName)) {
      e[`models.${i}.shortName`] = 'Short name must be unique.';
    } else if (envName && svc.length + shortName.length + envName.length > AIP_NAME_BUDGET) {
      e[`models.${i}.shortName`] = `Service name (${svc.length}) + short name (${shortName.length}) `
        + `+ environment "${envName}" (${envName.length}) exceeds ${AIP_NAME_BUDGET} characters. `
        + `Inference profile name "${svc}-${shortName}-${envName}" would exceed 64 characters.`;
    }
    seen.add(shortName);
  });
  return e;
}

/** Validates "Compute roles" (step 2). */
export function validateComputeRoles(s: FmWizardState): Errors {
  const e: Errors = {};
  const nonEmpty = s.computeRoles.filter((r) => r.trim());
  if (nonEmpty.length === 0) {
    e['computeRoles.0'] = 'Add at least one IAM role ARN.';
  }
  s.computeRoles.forEach((r, i) => {
    if (r.trim() && !ARN_RE.test(r.trim()))
      e[`computeRoles.${i}`] = 'Enter a valid IAM role ARN (arn:aws:iam::<account>:role/<name>).';
  });
  return e;
}

/** Validates "Budget & notifications" (step 3). */
export function validateBudget(s: FmWizardState): Errors {
  const e: Errors = {};
  const amount = Number(s.budgetAmount);
  if (!s.budgetAmount.trim() || Number.isNaN(amount) || amount <= 0)
    e.budgetAmount = 'Enter an amount greater than 0.';
  s.businessEmails.forEach((em, i) => {
    if (em.trim() && !EMAIL_RE.test(em.trim()))
      e[`businessEmails.${i}`] = 'Enter a valid email address.';
  });
  return e;
}

/** Validates a given step index; returns the errors object (empty = valid). */
export function validateStep(
  step: number,
  s: FmWizardState,
  environments: Array<{ documentId: string; name: string }> = [],
): Errors {
  switch (step) {
    case 0: return validateBundleInfo(s, environments);
    case 1: return validateModels(s, environments);
    case 2: return validateComputeRoles(s);
    case 3: return validateBudget(s);
    default: return {};
  }
}
